import { AlertBS, Spinner } from "@biblioteksentralen/react";
import { isAtLeastOne } from "@biblioteksentralen/types";
import { LCWork, LCWorkTriple, isLCWork } from "@libry-content/integrations";
import { RefObject } from "react";
import useSWR from "swr";
import ErrorBoundary from "../../../components/errorPages/ErrorBoundary";
import { useCommonData } from "../../../components/layout/CommonDataProvider";
import { DialogLC } from "../../../components/ModalContainer";
import { LoginForm } from "../../../rediaPlatform/LoginForm";
import { useRediaPlatformContext } from "../../../rediaPlatform/RediaPlatformProvider";
import { useTranslation } from "../../../utils/hooks/useTranslation";
import { getSearchConfig } from "../../searchApi/searchConfig";
import { getWork } from "../../searchApi/searchWorks";
import FavoriteDialogBody from "./FavoriteDialogBody";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
  workOrWorkId: LCWork | string;
  onCloseFocusRef: RefObject<HTMLDivElement | null>;
  popoverTrigger: React.ReactNode;
};

export const FavoriteDialog = ({ isOpen, onClose, onToggle, workOrWorkId, onCloseFocusRef, popoverTrigger }: Props) => {
  const { t } = useTranslation();
  const { user, isSessionReady } = useRediaPlatformContext();

  const headingTitle = t("Legg i mine lister");

  if (isSessionReady && !user) {
    return (
      <DialogLC
        heading={headingTitle}
        open={isOpen}
        onOpenChange={onToggle}
        finalFocusEl={() => onCloseFocusRef.current}
        hideCloseButton
        trigger={popoverTrigger}
      >
        <LoginForm />
      </DialogLC>
    );
  }

  return (
    <DialogLC
      trigger={popoverTrigger}
      heading={headingTitle}
      open={isOpen}
      onOpenChange={onToggle}
      lazyMount // Bruker lazyMount for lazy loading for å unngå at FavoriteDialogBody lastes inn før den er synlig.
      hideCloseButton
    >
      {isLCWork(workOrWorkId) ? (
        <FavoriteDialogWrapper work={workOrWorkId} onClose={onClose} />
      ) : (
        <WorkIdToWorkWrapper workId={workOrWorkId} onClose={onClose} />
      )}
    </DialogLC>
  );
};

const WorkIdToWorkWrapper = ({ workId, onClose }: { workId: string; onClose: () => void }) => {
  const { t } = useTranslation();
  const site = useCommonData().site;
  const searchConfig = getSearchConfig(site);

  const { data: work, error } = useSWR([workId, searchConfig], async () => {
    return await getWork(searchConfig, workId);
  });

  if (error)
    return (
      <AlertBS status="warning">{t("Beklager, det er ikke mulig å legge til denne i listen akkurat nå.")}</AlertBS>
    );

  if (!work) return <Spinner />;

  return <FavoriteDialogWrapper work={work} onClose={onClose} />;
};

// Lazy loading wrapper
const FavoriteDialogWrapper = ({ work, onClose }: { work: LCWork; onClose: () => void }) => {
  const variants = work.getVariantsWithTriples();
  //Bruk workTriples av mest relevante variant til huskeliste
  const workTriplesFromVariant = variants.find(({ workTriples }) => workTriples)?.workTriples;

  return (
    <ErrorBoundary boundaryName="FavoriteDialogWrapper">
      {isAtLeastOne<LCWorkTriple>(workTriplesFromVariant) && (
        <FavoriteDialogBody work={work} workTriples={workTriplesFromVariant} onClose={onClose} />
      )}
    </ErrorBoundary>
  );
};
